import { TimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { produce } from 'immer';
import moment, { Moment } from 'moment';
import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '../../../../../../../common/global/helpers';
import DatePicker from '../../../../../../../common/widgets/DatePicker';
import Dropdown from '../../../../../../../common/widgets/Dropdown';
import Icons from '../../../../../../../common/widgets/Icons';
import PopoverDialog from '../../../../../../../common/widgets/PopoverDialog';
import TimePicker from '../../../../../../../common/widgets/TimePicker';
import { ErrorState, ValidationError } from '../../../../../models/Errors';
import {
  PresenceTypes,
  Timesheet,
} from '../../../../../models/TimeAndAttendance';
import { resources } from '../../../../../resources/resources';
import { timeAndAttendanceService } from '../../../../../services/time-and-attendance';
import { TimeAndAttendanceStep } from '../enum';

interface ITimeAndAttendanceFormProps {
  selectedEvent: Timesheet | undefined;
  setSelectedEvent: React.Dispatch<React.SetStateAction<Timesheet | undefined>>;
  setActiveStep: React.Dispatch<React.SetStateAction<TimeAndAttendanceStep>>;
  setShowAttendanceDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setTimesheet: React.Dispatch<React.SetStateAction<Timesheet[] | undefined>>;
  currentDate: Moment;
  availableTimesheetPresenceTypes: PresenceTypes[];
  errors: ErrorState;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
}

export const TimeAndAttendanceForm = (props: ITimeAndAttendanceFormProps) => {
  const { t: translate } = useTranslation();

  const currentDate = new Date();

  const normalizeDate = (value: unknown, fallback: Date) => {
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsedDate = new Date(value);

      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }

    return fallback;
  };

  const attendanceState = useMemo(() => {
    return {
      eventType: props.selectedEvent?.presenceType,
      startDate: normalizeDate(props.selectedEvent?.startDate, new Date()),
      endDate: normalizeDate(props.selectedEvent?.endDate, new Date()),
      startHour: (() => {
        const currentDate = new Date();
        const [hours, minutes, sec] =
          props.selectedEvent?.workEvent?.startWorkHour?.split(':') ?? [
            '00',
            '00',
            '00',
          ];
        currentDate.setHours(
          Number.parseInt(hours),
          Number.parseInt(minutes),
          Number.parseInt(sec),
        );

        return currentDate;
      })(),
      endHour: (() => {
        const currentDate = new Date();
        const [hours, minutes, sec] =
          props.selectedEvent?.workEvent?.endWorkHour?.split(':') ?? [
            '00',
            '00',
            '00',
          ];
        currentDate.setHours(
          Number.parseInt(hours),
          Number.parseInt(minutes),
          Number.parseInt(sec),
        );
        return currentDate;
      })(),
      startBreak: (() => {
        const currentDate = new Date();
        const [hours, minutes] =
          props.selectedEvent?.workEvent?.startBreakHour?.split(':') ?? [
            '00',
            '00',
            '00',
          ];
        currentDate.setHours(Number.parseInt(hours), Number.parseInt(minutes));
        return currentDate;
      })(),
      endBreak: (() => {
        const currentDate = new Date();
        const [hours, minutes] =
          props.selectedEvent?.workEvent?.endBreakHour?.split(':') ?? [
            '00',
            '00',
            '00',
          ];
        currentDate.setHours(Number.parseInt(hours), Number.parseInt(minutes));
        return currentDate;
      })(),
    };
  }, [props.selectedEvent]);

  const handleEventTypeChange = (e: DropDownListChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.presenceType = e.value;
        }
      }),
    );
  };

  const handleStartDateChange = (value: Date | null) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft && value) {
          draft.startDate = value;
        }
      }),
    );
  };

  const handleEndDateChange = (value: Date | null) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft && value) {
          draft.endDate = value;
        }
      }),
    );
  };

  const getHoursOrMinutesWithTwoDigits = (value: number) => {
    return (value < 10 ? '0' : '') + value;
  };

  const handleStartHourChange = (e: TimePickerChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.workEvent.startWorkHour = e.target.value
            ? getHoursOrMinutesWithTwoDigits(e.target.value.getHours()) +
              ':' +
              getHoursOrMinutesWithTwoDigits(e.target.value.getMinutes()) +
              ':00'
            : '00:00:00';
        }
      }),
    );
  };

  const handleEndHourChange = (e: TimePickerChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.workEvent.endWorkHour = e.target.value
            ? getHoursOrMinutesWithTwoDigits(e.target.value.getHours()) +
              ':' +
              getHoursOrMinutesWithTwoDigits(e.target.value.getMinutes()) +
              ':00'
            : '00:00:00';
        }
      }),
    );
  };

  const handleStartBreakChange = (e: TimePickerChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.workEvent.startBreakHour = e.target.value
            ? getHoursOrMinutesWithTwoDigits(e.target.value.getHours()) +
              ':' +
              getHoursOrMinutesWithTwoDigits(e.target.value.getMinutes()) +
              ':00'
            : '00:00:00';
        }
      }),
    );
  };

  const handleEndBreakChange = (e: TimePickerChangeEvent) => {
    props.setSelectedEvent(
      produce(draft => {
        if (draft) {
          draft.workEvent.endBreakHour = e.target.value
            ? getHoursOrMinutesWithTwoDigits(e.target.value.getHours()) +
              ':' +
              getHoursOrMinutesWithTwoDigits(e.target.value.getMinutes()) +
              ':00'
            : '00:00:00';
        }
      }),
    );
  };

  const handleRemoveEvent = async () => {
    if (props.selectedEvent?.id) {
      const abortController = new AbortController();

      const result =
        await timeAndAttendanceService.removeTimesheetPresenceEvent(
          props.selectedEvent?.id,
          {
            signal: abortController?.signal,
          },
        );

      if (abortController?.signal.aborted) {
        return;
      }

      if (!result.hasErrors) {
        if (result.data) {
          props.setTimesheet(prevState => {
            if (prevState) {
              return [
                ...prevState.filter(t => t.id !== props.selectedEvent?.id),
              ];
            } else {
              return [];
            }
          });
        }
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    }

    props.setShowAttendanceDialog(false);
  };

  const validateForm = (): boolean => {
    props.setErrors(
      produce(d => {
        if (!props.selectedEvent?.presenceType) {
          d.presenceTypeId = [getRequiredValidationError('presenceTypeId')];
        } else {
          delete d.presenceTypeId;
        }

        if (
          !props.selectedEvent?.startDate ||
          !moment(props.selectedEvent?.startDate, 'YYYY-MM-DD').isValid()
        ) {
          d.workDataStartDate = [
            getRequiredValidationError('workDataStartDate'),
          ];
        } else {
          delete d.workDataStartDate;
        }

        if (
          !props.selectedEvent?.endDate ||
          !moment(props.selectedEvent?.endDate, 'YYYY-MM-DD').isValid()
        ) {
          d.workDataEndDate = [getRequiredValidationError('workDataEndDate')];
        } else {
          delete d.workDataEndDate;
        }
      }),
    );

    return (
      !props.selectedEvent?.presenceType ||
      !props.selectedEvent?.startDate ||
      !moment(props.selectedEvent?.startDate, 'YYYY-MM-DD').isValid() ||
      !props.selectedEvent?.endDate ||
      !moment(props.selectedEvent?.endDate, 'YYYY-MM-DD').isValid()
    );
  };

  const getRequiredValidationError = (target: string): ValidationError => ({
    target,
    code: resources.validation.required,
    severity: 'error',
    placeholders: {},
  });

  return (
    <div
      className={`taa-form-container ${props.selectedEvent?.id && 'form-edit'}`}
    >
      <Dropdown
        mandatory
        label={
          props.selectedEvent?.id
            ? translate(resources.timeAndAttendance.editWorkTypeLabel)
            : translate(resources.timeAndAttendance.workTypeLabel)
        }
        data={props.availableTimesheetPresenceTypes}
        textField="presenceTypeDescription"
        dataItemKey="presenceTypeId"
        value={props.selectedEvent?.presenceType}
        onChange={handleEventTypeChange}
        errors={props.errors.presenceTypeId}
      />

      <label>
        {translate(resources.timeAndAttendance.workDaysLabel)}
        <sup>*</sup>
      </label>
      <div className="work-days-interval-container">
        <DatePicker
          value={attendanceState.startDate || currentDate}
          onChange={handleStartDateChange}
          maxDate={
            !props.selectedEvent?.id ? attendanceState.endDate : undefined
          }
          minDate={props.currentDate.startOf('month').toDate()}
          errors={props.errors.workDataStartDate}
        />
        {!props.selectedEvent?.id && (
          <>
            <span className="btn-icon">
              <Icons.ArrowRight />
            </span>
            <DatePicker
              value={attendanceState.endDate || currentDate}
              onChange={handleEndDateChange}
              minDate={attendanceState.startDate}
              maxDate={props.currentDate.endOf('month').toDate()}
              errors={props.errors.workDataEndDate}
            />
          </>
        )}
      </div>
      <label>{translate(resources.timeAndAttendance.workHoursLabel)}</label>
      <div className="work-hours-interval-container">
        <TimePicker
          value={new Date(attendanceState.startHour)}
          onChange={handleStartHourChange}
          max={
            attendanceState.endHour.getHours() != 0
              ? new Date(attendanceState.endHour)
              : new Date(
                  attendanceState.endHour.getFullYear(),
                  attendanceState.endHour.getMonth(),
                  attendanceState.endHour.getDay(),
                  23,
                )
          }
          errors={props.errors.startWorkHour}
          popupSettings={{
            appendTo:
              (document.getElementsByClassName('k-dialog')[0] as HTMLElement) ??
              undefined,
          }}
        />

        <span className="btn-icon">
          <Icons.ArrowRight />
        </span>
        <TimePicker
          mandatory
          value={new Date(attendanceState.endHour)}
          onChange={handleEndHourChange}
          min={new Date(attendanceState.startHour)}
          errors={props.errors.endWorkHour}
          popupSettings={{
            appendTo:
              (document.getElementsByClassName('k-dialog')[0] as HTMLElement) ??
              undefined,
          }}
        />
      </div>

      <label>{translate(resources.timeAndAttendance.breakHoursLabel)}</label>

      <div className="pause-interval-container">
        <TimePicker
          value={new Date(attendanceState.startBreak)}
          onChange={handleStartBreakChange}
          max={
            attendanceState.endBreak.getHours() != 0
              ? new Date(attendanceState.endBreak)
              : new Date(
                  attendanceState.endHour.getFullYear(),
                  attendanceState.endHour.getMonth(),
                  attendanceState.endHour.getDay(),
                  23,
                )
          }
          errors={props.errors.startBreakHour}
          popupSettings={{
            appendTo:
              (document.getElementsByClassName('k-dialog')[0] as HTMLElement) ??
              undefined,
          }}
        />

        <span className="btn-icon">
          <Icons.ArrowRight />
        </span>
        <TimePicker
          mandatory
          value={new Date(attendanceState.endBreak)}
          onChange={handleEndBreakChange}
          min={new Date(attendanceState.startBreak)}
          errors={props.errors.endBreakHour}
          popupSettings={{
            appendTo:
              (document.getElementsByClassName('k-dialog')[0] as HTMLElement) ??
              undefined,
          }}
        />
      </div>

      <div className="taa-form-actions">
        {props.selectedEvent?.id && (
          <PopoverDialog
            className="info-tooltip"
            placement="bottom"
            type="hover"
            title={translate(resources.timeAndAttendance.delete)}
          >
            <button className="btn btn-delete" onClick={handleRemoveEvent}>
              <span className="btn-icon">
                <Icons.Delete />
              </span>
            </button>
          </PopoverDialog>
        )}
        <button
          className={`btn btn-solid ${
            !props.selectedEvent?.id && 'btn-nextstep'
          }`}
          onClick={() => {
            if (validateForm()) return;
            props.setActiveStep(TimeAndAttendanceStep.Confirm);
          }}
        >
          {props.selectedEvent?.id
            ? translate(resources.timeAndAttendance.save)
            : translate(resources.timeAndAttendance.add)}
        </button>
      </div>
    </div>
  );
};
